export interface User {
  id: number;
  email: string;
  role: 'admin' | 'nurse';
  name?: string;
  short?: string;
  display_name?: string;
  first_name?: string;
  last_name?: string;
  full_name?: string;
}

export interface AuthState {
  isAuthenticated: boolean;
  user: User | null;
}

export function getDeviceToken() {
  let token = localStorage.getItem("device_token");

  if (!token) {
    token = crypto.randomUUID();
    localStorage.setItem("device_token", token);
  }

  return token;
}

function decodeJwtPayload(token: string): { exp?: number } | null {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const payload = parts[1];
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
    const json = atob(padded);
    return JSON.parse(json);
  } catch {
    return null;
  }
}

class AuthService {

private readonly API_URL =
  process.env.REACT_APP_API_URL || 'https://app.thuisverpleging.be';


  private readonly USER_KEY = 'auth_user';
  private readonly TOKEN_KEY = 'auth_token';

  async login(email: string, password: string): Promise<User> {
    const res = await fetch(`${this.API_URL}/api/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password })
    });

    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.message || "Ongeldige inloggegevens");
    }

    const { user, token } = await res.json();

    localStorage.setItem(this.TOKEN_KEY, token);
    localStorage.setItem(this.USER_KEY, JSON.stringify(user));

    return user;
  }

  async loginStart(email: string, password: string) {

    const deviceToken = getDeviceToken();

    const res = await fetch(`${this.API_URL}/api/auth/login-start`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password, deviceToken })
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || "Login-start failed");
    }

    const data = await res.json();

    return {
      requiresOtp: !!data.requiresOtp,
      sessionId: data.sessionId ?? ""
    };
  }

  async loginVerify(sessionId: string, otp: string) {

    const deviceToken = getDeviceToken();

    const res = await fetch(`${this.API_URL}/api/auth/login-verify`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        sessionId,
        otp,
        deviceToken
      })
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || "Invalid OTP code");
    }

    return res.json();
  }

  saveUser(user: User, token: string) {
    localStorage.setItem(this.TOKEN_KEY, token);
    localStorage.setItem(this.USER_KEY, JSON.stringify(user));
  }

  getToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  getTokenExpiryMs(token?: string): number | null {
    const value = token ?? this.getToken();
    if (!value) return null;
    const payload = decodeJwtPayload(value);
    if (!payload || typeof payload.exp !== "number") return null;
    return payload.exp * 1000;
  }

  isTokenExpired(token?: string): boolean {
    const expiryMs = this.getTokenExpiryMs(token);
    if (!expiryMs) return true;
    return Date.now() >= expiryMs;
  }

  logout() {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);
  }

  getCurrentUser(): User | null {
    try {
      const stored = localStorage.getItem(this.USER_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  }

  isAuthenticated(): boolean {
    const token = this.getToken();
    if (!token) return false;
    return !this.isTokenExpired(token);
  }
}

export const authService = new AuthService();

// CLEAN, SINGLE exports
export const loginStart = authService.loginStart.bind(authService);
export const loginVerify = authService.loginVerify.bind(authService);
